import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api, resolveImage } from "@/lib/api";
import { useBrands } from "./Yachts";
import { useSiteNav } from "./Navbar";

const SLIDE_MS = 5000;

export const youtubeId = (url = "") => {
  const m = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : "";
};

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span className="block" initial={{ y: "115%" }} animate={{ y: "0%" }} exit={{ y: "-115%", transition: { duration: 0.6 } }} transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}>
      {children}
    </motion.span>
  </span>
);

let ytReady;
const loadYT = () => {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (!ytReady) {
    ytReady = new Promise((resolve) => {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { prev?.(); resolve(window.YT); };
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(tag);
    });
  }
  return ytReady;
};

export const isFileVideo = (url = "") => /\.(mp4|webm|mov)(\?|$)/i.test(url);

const ImageBackdrop = ({ slide, poster }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.4, ease: "easeInOut" }} className="absolute inset-0 overflow-hidden bg-navy-ink" data-testid={`hero-video-${slide.brand_slug}`} data-video-state="image">
    <motion.img
      src={resolveImage(poster)}
      alt=""
      initial={{ scale: 1.04 }}
      animate={{ scale: 1.16 }}
      transition={{ duration: SLIDE_MS / 1000 + 2, ease: "linear" }}
      className="absolute inset-0 h-full w-full object-cover"
      data-testid={`hero-poster-${slide.brand_slug}`}
    />
  </motion.div>
);

const FileBackdrop = ({ slide, poster }) => {
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.4, ease: "easeInOut" }} className="absolute inset-0 overflow-hidden bg-navy-ink" data-testid={`hero-video-${slide.brand_slug}`} data-video-state={failed ? "fallback" : playing ? "playing" : "loading"}>
      {poster && <img src={resolveImage(poster)} alt="" className="absolute inset-0 h-full w-full scale-105 object-cover" data-testid={`hero-poster-${slide.brand_slug}`} />}
      <video
        key={slide.video_url}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        onPlaying={() => { setPlaying(true); setFailed(false); }}
        onError={(e) => { if (e.currentTarget.networkState === 3 || e.currentTarget.error) setFailed(true); }}
        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1500ms] ${playing && !failed ? "opacity-100" : "opacity-0"}`}
        data-testid={`hero-video-${slide.brand_slug}-el`}
      >
        <source src={resolveImage(slide.video_url)} type={slide.video_url.match(/\.webm/i) ? "video/webm" : "video/mp4"} />
        {/\.mp4$/i.test(slide.video_url) && <source src={resolveImage(slide.video_url.replace(/\.mp4$/i, ".webm"))} type="video/webm" />}
      </video>
    </motion.div>
  );
};

const VideoBackdrop = ({ slide, poster }) => {
  const id = youtubeId(slide.video_url);
  const holder = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!id || !holder.current) return;
    let player; let cancelled = false;
    const timeout = setTimeout(() => setFailed(true), 9000);
    const mount = document.createElement("div");
    holder.current.appendChild(mount);
    loadYT().then((YT) => {
      if (cancelled) return;
      player = new YT.Player(mount, {
        videoId: id,
        playerVars: { autoplay: 1, mute: 1, controls: 0, loop: 1, playlist: id, rel: 0, modestbranding: 1, playsinline: 1, disablekb: 1, fs: 0, iv_load_policy: 3, start: slide.start || 0 },
        events: {
          onReady: (e) => { e.target.mute(); e.target.playVideo(); },
          onStateChange: (e) => { if (e.data === 1) { setPlaying(true); setFailed(false); clearTimeout(timeout); } },
          onError: () => setFailed(true),
        },
      });
    });
    return () => { cancelled = true; clearTimeout(timeout); try { player?.destroy(); } catch {} mount.remove(); };
  }, [id, slide.start]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.4, ease: "easeInOut" }} className="absolute inset-0 overflow-hidden bg-navy-ink" data-testid={`hero-video-${slide.brand_slug}`} data-video-state={failed ? "fallback" : playing ? "playing" : "loading"}>
      {poster && <img src={resolveImage(poster)} alt="" className="absolute inset-0 h-full w-full scale-105 object-cover" data-testid={`hero-poster-${slide.brand_slug}`} />}
      <div
        ref={holder}
        className={`pointer-events-none absolute left-1/2 top-1/2 h-[max(100%,56.25vw)] w-[max(100%,177.78vh)] -translate-x-1/2 -translate-y-1/2 scale-[1.02] transition-opacity duration-[1500ms] [&_iframe]:h-full [&_iframe]:w-full ${playing && !failed ? "opacity-100" : "opacity-0"}`}
      />
    </motion.div>
  );
};

export default function Hero() {
  const ref = useRef(null);
  const goTo = useSiteNav();
  const { data } = useQuery({ queryKey: ["hero-slides"], queryFn: async () => (await api.get("/hero-slides")).data });
  const { data: brands = [] } = useBrands();
  const slides = useMemo(() => (data || []).filter((s) => youtubeId(s.video_url) || isFileVideo(s.video_url) || s.poster), [data]);
  const copy = slides[0] || {};
  const [index, setIndex] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % slides.length), SLIDE_MS);
    return () => clearInterval(t);
  }, [slides.length]);

  const slide = slides[index] || slides[0];

  return (
    <section id="hero" ref={ref} className="relative h-screen max-h-screen overflow-hidden bg-navy-ink" data-testid="hero-section">
      <AnimatePresence>
        {slide && (() => {
          const poster = slide.poster || brands.find((b) => b.slug === slide.brand_slug)?.hero_image;
          if (isFileVideo(slide.video_url)) return <FileBackdrop key={slide.video_url} slide={slide} poster={poster} />;
          if (youtubeId(slide.video_url)) return <VideoBackdrop key={youtubeId(slide.video_url)} slide={slide} poster={poster} />;
          return <ImageBackdrop key={`img-${slide.brand_slug}-${index}`} slide={slide} poster={poster} />;
        })()}
      </AnimatePresence>
      <div className="pointer-events-none absolute inset-0 bg-black/30" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/30" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-navy to-transparent" />

      <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 flex h-full flex-col justify-end px-6 pb-28 pt-28 lg:px-12 lg:pb-32">
        <div className="max-w-7xl">
          {slide && (
            <div data-testid="hero-slide-copy" data-active-brand={slide.brand_slug}>
              <MaskedLine delay={0.2}>
                <span className="mb-5 block text-[10px] uppercase tracking-[0.45em] text-gold-pale sm:text-xs" data-testid="hero-overline">
                  Modi Auto Group Presents
                </span>
              </MaskedLine>
              <h1 className="font-serif font-light leading-[1] tracking-tighter text-white" data-testid="hero-heading">
                <MaskedLine delay={0.35}><span className="block text-[clamp(2.25rem,min(9vh,12vw),6rem)]">{copy.line1}</span></MaskedLine>
                <MaskedLine delay={0.5}><span className="block text-[clamp(2.25rem,min(9vh,12vw),6rem)] italic text-gold-pale">{copy.line2}</span></MaskedLine>
                <MaskedLine delay={0.65}><span className="block text-[clamp(2.25rem,min(9vh,12vw),6rem)]">{copy.line3}</span></MaskedLine>
              </h1>
              <MaskedLine delay={0.85}>
                <p className="mt-5 max-w-xl text-sm font-light leading-relaxed text-white/75 sm:text-base" data-testid="hero-subtext">{copy.subtext}</p>
              </MaskedLine>
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.1, ease: [0.16, 1, 0.3, 1] }} className="mt-7 flex flex-wrap items-center gap-5 lg:mt-8">
                <button onClick={() => goTo(copy.cta_href || "/yachts")} data-testid="hero-explore-fleet-button" className="border border-gold bg-gold px-9 py-4 text-[11px] uppercase tracking-[0.3em] text-navy transition-colors duration-300 hover:bg-transparent hover:text-gold">
                  {copy.cta_label || "Explore the Fleet"}
                </button>
                <button onClick={() => goTo("#contact")} data-testid="hero-enquire-button" className="border border-white/30 px-9 py-4 text-[11px] uppercase tracking-[0.3em] text-white transition-colors duration-300 hover:border-gold hover:text-gold">
                  Enquire
                </button>
              </motion.div>
            </div>
          )}
        </div>
      </motion.div>

      {slides.length > 1 && (
        <div className="absolute bottom-8 left-6 right-6 z-10 flex items-center justify-between gap-6 lg:left-12 lg:right-12" data-testid="hero-slider-controls">
          <div className="flex items-center gap-4 text-[10px] uppercase tracking-[0.3em] text-white/70" data-testid="hero-slide-counter">
            <span className="text-gold-pale">{String(index + 1).padStart(2, "0")}</span>
            <span className="relative block h-px w-16 bg-white/25 sm:w-24">
              <motion.span key={index} initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: SLIDE_MS / 1000, ease: "linear" }} className="absolute inset-0 origin-left bg-gold-pale" />
            </span>
            <span>{String(slides.length).padStart(2, "0")}</span>
          </div>
          <button onClick={() => goTo("#legacy")} data-testid="hero-scroll-indicator" aria-label="Scroll down" className="rounded-full border border-white/40 bg-black/25 p-2.5 text-white/80 backdrop-blur-sm transition-colors hover:text-gold-pale">
            <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }} className="block"><ArrowDown size={18} strokeWidth={1.2} /></motion.span>
          </button>
        </div>
      )}
    </section>
  );
}
